import { useCallback, useEffect, useState } from "react";
import { useCanvas } from "../contexts/CanvasContext";
import { useChat } from "../contexts/ChatContext";
import { canvasEventEmitter } from "../lib/ai/tools/canvasStreamingTool";

/**
 * Hook for integrating canvas functionality with chat
 * Provides methods to enable/disable canvas mode and handle canvas streaming
 */
export const useCanvasIntegration = () => {
  // useCanvas now returns a safe fallback if CanvasProvider is not available
  const { canvasState, openCanvas, closeCanvas } = useCanvas();
  const chatContext = useChat();

  // Use canvas mode state from ChatContext instead of local state
  const isCanvasModeEnabled = chatContext?.isCanvasModeEnabled || false;
  const enableCanvasMode = chatContext?.enableCanvasMode || (() => {});
  const disableCanvasMode = chatContext?.disableCanvasMode || (() => {});

  const [streamingSettings, setStreamingSettings] = useState({
    useStructuredOutput: true,
    autoDetectContent: true,
    preferredType: "text",
  });

  /**
   * Enable canvas mode with structured streaming
   */
  const enableCanvasModeWithSettings = useCallback(
    (settings = {}) => {
      const finalSettings = {
        ...streamingSettings,
        ...settings,
      };

      // Use ChatContext's enableCanvasMode function
      enableCanvasMode();
      setStreamingSettings(finalSettings);

      // Update chat context if available
      if (chatContext?.updateContextData) {
        chatContext.updateContextData({
          isCanvasModeEnabled: true,
          useStructuredOutput: finalSettings.useStructuredOutput,
          canvasSettings: finalSettings,
        });
      }
    },
    [streamingSettings, chatContext, enableCanvasMode]
  );

  /**
   * Disable canvas mode (wrapper around ChatContext function)
   */
  const disableCanvasModeWithCleanup = useCallback(() => {
    // Use ChatContext's disableCanvasMode function
    disableCanvasMode();

    // Update chat context if available
    if (chatContext?.updateContextData) {
      chatContext.updateContextData({
        isCanvasModeEnabled: false,
        useStructuredOutput: false,
        canvasSettings: null,
      });
    }

    // Close canvas if open
    if (canvasState.isOpen) {
      closeCanvas();
    }
  }, [chatContext, canvasState.isOpen, closeCanvas, disableCanvasMode]);

  /**
   * Toggle canvas mode
   */
  const toggleCanvasMode = useCallback(() => {
    if (isCanvasModeEnabled) {
      disableCanvasModeWithCleanup();
    } else {
      enableCanvasModeWithSettings();
    }
  }, [
    isCanvasModeEnabled,
    enableCanvasModeWithSettings,
    disableCanvasModeWithCleanup,
  ]);

  /**
   * Request canvas stream with specific query
   */
  const requestCanvasStream = useCallback(
    async (query, options = {}) => {
      if (!isCanvasModeEnabled) {
        enableCanvasModeWithSettings();
      }

      const streamSettings = {
        query,
        type: options.type || streamingSettings.preferredType,
        language: options.language || "javascript",
        useStructuredOutput:
          options.useStructuredOutput ?? streamingSettings.useStructuredOutput,
        streamId: options.streamId || `stream_${Date.now()}`,
        ...options,
      };

      // Update chat context for this request
      if (chatContext?.updateContextData) {
        chatContext.updateContextData({
          isCanvasModeEnabled: true,
          useStructuredOutput: streamSettings.useStructuredOutput,
          forceCanvasStream: true,
          canvasStreamSettings: streamSettings,
        });
      }

      // If we have a sendMessage function in chat context, use it
      if (chatContext?.sendMessage) {
        return chatContext.sendMessage(query, {
          forceCanvasStream: true,
          canvasStreamSettings: streamSettings,
        });
      }

      // Otherwise, emit a canvas stream request event
      canvasEventEmitter.emit("canvas_stream_request", streamSettings);

      return streamSettings;
    },
    [
      isCanvasModeEnabled,
      enableCanvasModeWithSettings,
      streamingSettings,
      chatContext,
    ]
  );

  /**
   * Create canvas with content directly
   */
  const createCanvas = useCallback(
    (content, type = "text", options = {}) => {
      if (!isCanvasModeEnabled) {
        enableCanvasModeWithSettings();
      }

      // Use the new openCanvas API structure
      openCanvas({
        content,
        type,
        language: options.language || "javascript",
        metadata: {
          ...options,
          source: "direct_creation",
          timestamp: new Date().toISOString(),
        },
      });
    },
    [isCanvasModeEnabled, enableCanvasModeWithSettings, openCanvas]
  );

  /**
   * Update streaming settings
   */
  const updateStreamingSettings = useCallback(
    (newSettings) => {
      setStreamingSettings((prev) => ({
        ...prev,
        ...newSettings,
      }));

      // Update chat context if canvas mode is enabled
      if (isCanvasModeEnabled && chatContext?.updateContextData) {
        chatContext.updateContextData({
          useStructuredOutput:
            newSettings.useStructuredOutput ??
            streamingSettings.useStructuredOutput,
          canvasSettings: {
            ...streamingSettings,
            ...newSettings,
          },
        });
      }
    },
    [isCanvasModeEnabled, streamingSettings, chatContext]
  );

  /**
   * Auto-detect canvas triggers in messages
   */
  const shouldTriggerCanvas = useCallback(
    (message) => {
      if (!streamingSettings.autoDetectContent) {
        return false;
      }

      const lowerMessage = message.toLowerCase();

      // Canvas trigger keywords
      const canvasTriggers = [
        "create canvas",
        "open canvas",
        "canvas with",
        "write code",
        "create document",
        "generate content",
        "write article",
        "create notes",
      ];

      return canvasTriggers.some((trigger) => lowerMessage.includes(trigger));
    },
    [streamingSettings.autoDetectContent]
  );

  /**
   * Handle auto-canvas triggering
   */
  useEffect(() => {
    const handleMessage = (message) => {
      if (shouldTriggerCanvas(message.content)) {
        enableCanvasModeWithSettings({
          autoTriggered: true,
          triggerMessage: message.content,
        });
      }
    };

    // Listen for messages if chat context provides it
    if (chatContext?.onMessage) {
      chatContext.onMessage(handleMessage);
    }

    return () => {
      if (chatContext?.offMessage) {
        chatContext.offMessage(handleMessage);
      }
    };
  }, [shouldTriggerCanvas, enableCanvasModeWithSettings, chatContext]);

  // Listen for canvas events and sync state
  useEffect(() => {
    const handleCanvasCreate = (data) => {
      // Only auto-enable canvas mode if this was triggered by a tool call in the current session
      // Don't auto-enable for historical canvas content or navigation events
      const isCurrentSessionToolCall =
        data?.metadata?.createdFromTool === true ||
        data?.metadata?.source === "tool_call";

      if (!isCanvasModeEnabled && isCurrentSessionToolCall) {
        enableCanvasMode();
      }
    };

    const handleCanvasClose = () => {
      // Don't disable canvas mode when canvas closes, keep it available
      //      console.log("🎨 Canvas closed but mode remains enabled");
    };

    canvasEventEmitter.on("canvas_create", handleCanvasCreate);
    canvasEventEmitter.on("canvas_close", handleCanvasClose);

    return () => {
      canvasEventEmitter.off("canvas_create", handleCanvasCreate);
      canvasEventEmitter.off("canvas_close", handleCanvasClose);
    };
  }, [isCanvasModeEnabled, enableCanvasMode]);

  return {
    // State
    isCanvasModeEnabled,
    streamingSettings,
    canvasState,

    // Actions
    enableCanvasMode: enableCanvasModeWithSettings,
    disableCanvasMode: disableCanvasModeWithCleanup,
    toggleCanvasMode,
    requestCanvasStream,
    createCanvas,
    updateStreamingSettings,

    // Utilities
    shouldTriggerCanvas,
  };
};

export default useCanvasIntegration;
